import { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Mail, Phone, Users as UsersIcon, ChevronRight, Trash2, Sparkles, Download, Tag as TagIcon, X } from "lucide-react";
import { ListSkeleton, HeaderSkeleton } from "@/components/Skeletons";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/exportCsv";
import FollowUpList from "@/components/FollowUpList";
import { CustomerImportDialog } from "@/components/CustomerImportDialog";
import { PANEL_CLS, ROW_CLS, PAGE_EYEBROW_CLS, PAGE_H1_CLS, PRIMARY_BTN_CLS, SECONDARY_BTN_CLS, Avatar } from "@/components/premium";

export default function Customers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isOwner = user?.role === "owner";
  const [bizPlan, setBizPlan] = useState(null);
  const isPremium = bizPlan === "premium";
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");      // exact-tag filter
  const [activeSegment, setActiveSegment] = useState(""); // built-in segment
  const [allTags, setAllTags] = useState([]);          // [{name, count}]
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });
  const [toDelete, setToDelete] = useState(null); // {customer, deleteBookings}
  const [deleteBookings, setDeleteBookings] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      // Send any combination of free-text search + tag + segment.
      // Backend AND-s them — empty params are skipped.
      const params = {};
      if (q) params.q = q;
      if (activeTag) params.tag = activeTag;
      if (activeSegment) params.segment = activeSegment;
      const res = await api.get("/customers", { params });
      setList(res.data);
    } catch { toast.error("Kunne ikke laste kunder"); }
    setLoading(false);
  };
  // Tag list — refreshed every time we successfully load the customer list
  // so newly-added tags appear in the filter chip-row without a hard reload.
  const refreshTags = async () => {
    try {
      const res = await api.get("/customers/tags");
      setAllTags(res.data || []);
    } catch { /* non-critical */ }
  };
  useEffect(() => { load(); refreshTags(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, activeTag, activeSegment]);
  // Deep-link from Dashboard quick actions: `/kunder?new=1` auto-opens the
  // "Ny kunde" modal. `?then_book=1` additionally chains to the booking
  // modal pre-filled with the newly-created customer on success.
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      // Consume the `new` flag so refresh / back-nav doesn't reopen it;
      // keep `then_book` until the submit handler has read it.
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);
  // Fetch the current plan once — controls the Premium-gated import button.
  useEffect(() => {
    if (!isOwner) return;
    let alive = true;
    api.get("/business")
      .then((r) => alive && setBizPlan(r.data?.plan || null))
      .catch(() => {});
    return () => { alive = false; };
  }, [isOwner]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Navn er påkrevd");
    try {
      // Omit empty optional fields so backend doesn't try to validate ""
      const payload = { name: form.name.trim() };
      if (form.phone?.trim()) payload.phone = form.phone.trim();
      if (form.email?.trim()) payload.email = form.email.trim();
      if (form.notes?.trim()) payload.notes = form.notes.trim();
      const res = await api.post("/customers", payload);
      const newId = res?.data?.id;
      toast.success("Kunde lagt til");
      setOpen(false); setForm({ name: "", phone: "", email: "", notes: "" });
      load();
      // If the user came in via the "Ny kunde + booking" quick action,
      // chain straight into the booking modal with the new customer
      // pre-selected. We pass customer_id so the Calendar page can
      // fetch + prefill the booking form fields.
      if (searchParams.get("then_book") === "1" && newId) {
        navigate(`/kalender?new=1&customer_id=${newId}`);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail.some((d) => String(d?.loc || []).includes("email"))) {
        toast.error("Ugyldig e-postadresse");
      } else {
        toast.error(typeof detail === "string" ? detail : "Kunne ikke lagre");
      }
    }
  };

  const askDelete = (customer, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteBookings(false);
    setToDelete(customer);
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/customers/${toDelete.id}`, {
        params: deleteBookings ? { delete_bookings: true } : {},
      });
      const removed = res.data?.bookings_removed || 0;
      toast.success(removed > 0 ? `Kunde og ${removed} booking(er) slettet` : "Kunde slettet");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke slette");
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div className="min-w-0">
          <p className={PAGE_EYEBROW_CLS}>Kunder</p>
          <h1 className={PAGE_H1_CLS}>Kundeoversikt</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isOwner && (
            <Button
              variant="outline"
              className={SECONDARY_BTN_CLS}
              onClick={() => downloadCsv("/export/customers.csv", "kunder.csv", "kunder")}
              data-testid="export-customers-btn"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Eksporter kunder
            </Button>
          )}
          {isOwner && (
            <CustomerImportDialog isPremium={isPremium} onDone={load} />
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className={PRIMARY_BTN_CLS} data-testid="new-customer-btn">
                <Plus className="w-4 h-4" strokeWidth={2.25} /> Ny kunde
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Ny kunde</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div><Label>Navn</Label><Input className="rounded-xl mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-input" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Telefon</Label><Input className="rounded-xl mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-input" /></div>
                  <div><Label>E-post</Label><Input type="email" className="rounded-xl mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-input" /></div>
                </div>
                <div><Label>Notater</Label><Textarea className="rounded-xl mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="customer-notes-input" /></div>
                <Button type="submit" className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="customer-submit-btn">Lagre</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search + filters wrapped in a premium panel */}
      <div className={`${PANEL_CLS} p-5 space-y-4`} data-testid="customer-filters-panel">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-10 rounded-xl max-w-md h-10 border-emerald-500/15 bg-background/60 focus-visible:ring-emerald-500/40"
            placeholder="Søk navn, telefon, e-post..."
            value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input"
          />
        </div>

        {/* Filter row — segments + tags. Both AND-combine with the free-text
            search. Clicking an active pill clears that single filter. */}
        <div className="space-y-2" data-testid="customer-filters">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mr-1">
              Segmenter
            </span>
            {[
              { id: "", label: "Alle" },
              { id: "new", label: "Nye (30d)" },
              { id: "upcoming", label: "Kommende time" },
              { id: "no_upcoming", label: "Ingen time bestilt" },
              { id: "inactive_60", label: "Inaktive 60d" },
              { id: "inactive_90", label: "Inaktive 90d" },
            ].map((s) => {
              const active = activeSegment === s.id;
              return (
                <button
                  key={s.id || "all"}
                  type="button"
                  onClick={() => setActiveSegment(active ? "" : s.id)}
                  className={`text-xs px-3 py-1 rounded-full border transition-all ${
                    active
                      ? "bg-emerald-500 text-white border-emerald-500 shadow-[0_4px_18px_-6px_rgba(16,185,129,0.55)]"
                      : "border-emerald-500/15 bg-emerald-500/[0.03] text-foreground hover:bg-emerald-500/[0.08]"
                  }`}
                  data-testid={`segment-filter-${s.id || "all"}`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {allTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mr-1 inline-flex items-center gap-1">
                <TagIcon className="w-3 h-3" strokeWidth={2} /> Tagger
              </span>
              {allTags.map((t) => {
                const active = activeTag.toLowerCase() === t.name.toLowerCase();
                return (
                  <button
                    key={t.name}
                    type="button"
                    onClick={() => setActiveTag(active ? "" : t.name)}
                    className={`text-xs px-3 py-1 rounded-full border transition-all inline-flex items-center gap-1.5 ${
                      active
                        ? "bg-emerald-500 text-white border-emerald-500 shadow-[0_4px_18px_-6px_rgba(16,185,129,0.55)]"
                        : "border-emerald-500/15 bg-emerald-500/[0.03] text-foreground hover:bg-emerald-500/[0.08]"
                    }`}
                    data-testid={`tag-filter-${t.name}`}
                  >
                    <span>{t.name}</span>
                    <span className={`text-[10px] ${active ? "opacity-80" : "text-muted-foreground"}`}>
                      {t.count}
                    </span>
                    {active && <X className="w-3 h-3" strokeWidth={2} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Kunder å følge opp — Pro/Demo feature. Component renders null on
          Basic (backend returns 402) so the layout stays clean. */}
      <FollowUpList />

      {loading ? (
        <ListSkeleton rows={6} />
      ) : list.length === 0 ? (
        <div className={`${PANEL_CLS} text-center py-16`} data-testid="customers-empty">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-500/30 items-center justify-center text-emerald-500 mb-3">
            <UsersIcon className="w-6 h-6" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-xl font-semibold">Ingen kunder enda</p>
          <p className="text-muted-foreground mt-1">Legg til kunder manuelt eller del bookinglenken din.</p>
          <Button className={`mt-6 ${PRIMARY_BTN_CLS}`} onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4" strokeWidth={2.25} /> Legg til første kunde
          </Button>
        </div>
      ) : (
        <div className={`${PANEL_CLS} overflow-hidden`}>
          <ul className="divide-y divide-emerald-500/10">
            {list.map((c) => (
              <li key={c.id}>
                <Link
                  to={`/kunder/${c.id}`}
                  className="flex items-center gap-4 p-4 hover:bg-emerald-500/[0.04] transition-colors"
                  data-testid="customer-row"
                >
                  <Avatar name={c.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{c.name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-3 mt-0.5">
                      {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                      {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                    </p>
                    {Array.isArray(c.tags) && c.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid="customer-row-tags">
                        {c.tags.slice(0, 4).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 ring-1 ring-emerald-500/25"
                          >
                            {t}
                          </span>
                        ))}
                        {c.tags.length > 4 && (
                          <span className="text-[10px] text-muted-foreground">+{c.tags.length - 4}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="hidden sm:block text-right text-xs text-muted-foreground tabular-nums">
                    <p>Sist: {c.last_visit ? formatDate(c.last_visit) : "—"}</p>
                    <p>Besøk: {c.total_visits}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/ai-meldinger", { state: { customerId: c.id } }); }}
                    className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-xl text-emerald-500 hover:bg-emerald-500/15 transition-colors"
                    aria-label={`Følg opp ${c.name}`}
                    title="Følg opp via AI"
                    data-testid="customer-followup-row-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  {isOwner && (
                    <button
                      type="button"
                      onClick={(e) => askDelete(c, e)}
                      className="ml-2 inline-flex items-center justify-center w-9 h-9 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                      aria-label={`Slett ${c.name}`}
                      data-testid="delete-customer-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Slett {toDelete?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Kunden vil bli fjernet fra kunderegisteret.
              {toDelete?.total_visits > 0 && (
                <span className="block mt-2">
                  Denne kunden har <strong>{toDelete.total_visits} fullført booking(er)</strong> i historikken.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3">
            <Checkbox
              id="delete-bookings"
              checked={deleteBookings}
              onCheckedChange={(v) => setDeleteBookings(!!v)}
              data-testid="delete-bookings-checkbox"
            />
            <div className="grid gap-0.5 leading-tight">
              <Label htmlFor="delete-bookings" className="cursor-pointer text-sm">
                Slett også all booking-historikk for denne kunden
              </Label>
              <p className="text-xs text-muted-foreground">
                Standard: bookinger beholdes med navn intakt — kun selve kundekortet fjernes.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="delete-customer-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-customer-confirm"
            >
              {deleting ? "Sletter..." : "Slett kunde"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
